import type { ApiResponse } from '@/types';

function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!envUrl) return '/api';
  if (envUrl.endsWith('/api')) return envUrl;
  return envUrl.replace(/\/$/, '') + '/api';
}

const API_BASE_URL = getApiBaseUrl();

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('auth_token');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  if (window.Telegram?.WebApp?.initData) {
    (headers as Record<string, string>)['X-Telegram-Init-Data'] = window.Telegram.WebApp.initData;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  const data: ApiResponse<T> = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Unknown error');
  }

  return data.data as T;
}

// ============================================
// TYPES
// ============================================

export interface JournalEntryOutput {
  id: string;
  eventId: string | null;
  whatIDid: string | null;
  whatWasNew: string | null;
  whatITake: string | null;
  nextStep: string | null;
  transferApplied: boolean | null;
  transferResult: string | null;
  transferCheckedAt: string | null;
  createdAt: string;
  needsTransferCheck: boolean;
}

export interface JournalEntriesResponse {
  entries: JournalEntryOutput[];
  total: number;
}

export interface CreateEntryInput {
  eventId?: string;
  whatIDid: string;
  whatWasNew: string;
  whatITake: string;
  nextStep: string;
}

export interface TransferInput {
  entryId: string;
  applied: boolean;
  result: string;
}

// ============================================
// API
// ============================================

export async function createJournalEntry(input: CreateEntryInput): Promise<JournalEntryOutput> {
  return request<JournalEntryOutput>('/journal/entry', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function processTransfer(input: TransferInput): Promise<JournalEntryOutput> {
  return request<JournalEntryOutput>('/journal/transfer', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getJournalEntries(limit = 20, offset = 0): Promise<JournalEntriesResponse> {
  return request<JournalEntriesResponse>(`/journal/entries?limit=${limit}&offset=${offset}`);
}
